import memoize from 'transmute/memoize';
import { propertyLabelTranslator } from 'property-translator/propertyTranslator';
import { OMIT, PIPELINE, BEST_CASE, COMMIT, CLOSED } from '../constants/ForecastCategories';
import { OMIT_THRESHOLD, PIPELINE_THRESHOLD, BEST_CASE_THRESHOLD, COMMIT_THRESHOLD, CLOSED_LOST_THRESHOLD, CLOSED_WON_THRESHOLD } from '../constants/WinProbabilities';
import { NORMAN, OZ } from '../constants/Colors';
import { FORECAST_CATEGORIES_STEP, CATEGORY_MAPPING_STEP, FINISH_STEP, WIZARD_FORECAST_CATEGORY } from '../constants/WizardStepTypes';
// @ts-expect-error untyped

import I18n from 'I18n';
import { DEAL_OBJECT_ID } from '../constants/CrmSearchConstants';
export const convertDealStageReferenceRecordList = data => {
  const dealStages = [];
  data.forEach(dealStage => {
    const stage = dealStage.get('referencedObject');
    dealStages.push({
      stageId: stage.get('stageId'),
      label: stage.get('label'),
      active: stage.get('active'),
      displayOrder: stage.get('displayOrder'),
      pipelineId: stage.get('pipeline').get('pipelineId'),
      objectTypeId: DEAL_OBJECT_ID,
      metadata: {
        isClosed: stage.get('closedWon'),
        probability: stage.get('probability')
      }
    });
  });
  return dealStages;
};
export const convertForecastCategoryReferenceRecordList = data => {
  const forecastCategories = [];
  data.getIn(['referencedObject', 'options']).forEach(category => {
    let description = '';
    switch (category.get('value')) {
      case OMIT:
        description = I18n.text('wizard.customize.table.categoryDefinitions.notForecast');
        break;
      case PIPELINE:
        description = I18n.text('wizard.customize.table.categoryDefinitions.pipeline');
        break;
      case BEST_CASE:
        description = I18n.text('wizard.customize.table.categoryDefinitions.bestCase');
        break;
      case COMMIT:
        description = I18n.text('wizard.customize.table.categoryDefinitions.commit');
        break;
      case CLOSED:
        description = I18n.text('wizard.customize.table.categoryDefinitions.closedWon');
        break;
      default:
        break;
    }
    forecastCategories.push({
      description,
      displayOrder: category.get('displayOrder'),
      doubleData: category.get('doubleData'),
      hidden: category.get('hidden'),
      label: category.get('label'),
      readOnly: category.get('readOnly'),
      value: category.get('value')
    });
  });
  return forecastCategories;
};
const getProbability = stage => {
  var _stage$metadata;
  return Number((_stage$metadata = stage.metadata) === null || _stage$metadata === void 0 ? void 0 : _stage$metadata.probability) * 100;
};
export const getDefaultForecastCategoryForDealStage = memoize((stage, categories) => {
  const probability = getProbability(stage);
  let defaultForecastCategory = OMIT;
  switch (true) {
    case probability <= CLOSED_LOST_THRESHOLD:
      defaultForecastCategory = OMIT;
      break;
    case probability < OMIT_THRESHOLD:
      defaultForecastCategory = OMIT;
      break;
    case probability < PIPELINE_THRESHOLD:
      defaultForecastCategory = PIPELINE;
      break;
    case probability < BEST_CASE_THRESHOLD:
      defaultForecastCategory = BEST_CASE;
      break;
    case probability < COMMIT_THRESHOLD:
      defaultForecastCategory = COMMIT;
      break;
    case probability === CLOSED_WON_THRESHOLD:
      defaultForecastCategory = CLOSED;
      break;
    default:
      break;
  }
  return categories.find(category => category.value === defaultForecastCategory) ? defaultForecastCategory : OMIT;
});
export const sortDealStageIds = (allStages, stageIdsToSort) => {
  const propabilityByStageId = {};
  allStages.forEach(stage => {
    propabilityByStageId[stage.stageId] = getProbability(stage);
  });
  return stageIdsToSort.sort((a, b) => propabilityByStageId[a] - propabilityByStageId[b]);
};
const getTagUse = memoize(probability => {
  switch (probability) {
    case CLOSED_LOST_THRESHOLD:
      return NORMAN;
    case CLOSED_WON_THRESHOLD:
      return OZ;
    default:
      return 'default';
  }
});
export const getDealStageOptions = memoize(dealStages => dealStages.sort((a, b) => getProbability(a) - getProbability(b)).map(dealStage => {
  const stageId = dealStage.stageId;
  const label = propertyLabelTranslator(dealStage.label);
  const probability = getProbability(dealStage);
  const tagUse = getTagUse(probability);
  return {
    text: label,
    dropdownText: `${label} (${probability}%)`,
    value: stageId,
    tagUse
  };
}));
export const getDefaultDealStagesByForecastCategoryMapping = (forecastCategories, dealStages) => {
  const dealStagesByForecastCategory = {};
  dealStages.forEach(stage => {
    const category = getDefaultForecastCategoryForDealStage(stage, forecastCategories);
    if (category) {
      const stageId = stage.stageId;
      dealStagesByForecastCategory[category] = category in dealStagesByForecastCategory ? [...dealStagesByForecastCategory[category], stageId] : [stageId];
    }
  });
  return dealStagesByForecastCategory;
};
export const getDefaultDealStageMappings = (dealStages, forecastCategories) => {
  const mappings = {};
  dealStages.forEach(stage => {
    const pipelineId = stage.pipelineId;
    const mapping = pipelineId in mappings ? mappings[pipelineId] : {
      automate: true,
      allDealStages: [],
      dealStageByForecastCategory: {}
    };
    mapping.allDealStages.push(stage);
    mappings[pipelineId] = mapping;
  });
  Object.keys(mappings).forEach(key => {
    const mapping = mappings[key];
    mapping.dealStageByForecastCategory = getDefaultDealStagesByForecastCategoryMapping(forecastCategories, mapping.allDealStages);
  });
  return mappings;
};
export const convertForecastCategoryValues = forecastCategories => forecastCategories.map((forecastCategory, index) => {
  const useNewKey = forecastCategory.useNewValue || forecastCategory.value.substring(0, WIZARD_FORECAST_CATEGORY.length) === WIZARD_FORECAST_CATEGORY;
  const forecastCategoryName = useNewKey ? forecastCategory.label.replaceAll(' ', '_').toUpperCase() : forecastCategory.value;
  return Object.assign({}, forecastCategory, {
    value: forecastCategoryName,
    description: '',
    displayOrder: index + 1,
    useNewValue: undefined,
    dealStageKey: forecastCategory.value
  });
});
export const createForecastCategoryPayload = forecastCategories => forecastCategories.map(({
  description,
  displayOrder,
  doubleData,
  hidden,
  label,
  readOnly,
  value
}) => {
  return {
    description,
    displayOrder,
    doubleData,
    hidden,
    label,
    readOnly,
    value
  };
});
export const buildMappingForForecastCategories = (forecastCategories, dealStagesByForecastCategory) =>
// only create mappings if there are mapped deal stages
forecastCategories.map(forecastCategory => {
  if (forecastCategory.dealStageKey && dealStagesByForecastCategory[forecastCategory.dealStageKey] && dealStagesByForecastCategory[forecastCategory.dealStageKey].length > 0) {
    return {
      forecastCategory: forecastCategory.value,
      dealStages: dealStagesByForecastCategory[forecastCategory.dealStageKey]
    };
  } else {
    return null;
  }
}).filter(category => category);
export const buildMapping = (pipelineId, forecastCategories, dealStageMapping) => dealStageMapping.automate ? {
  pipelineId,
  forecastCategories: buildMappingForForecastCategories(forecastCategories, dealStageMapping.dealStageByForecastCategory),
  enabled: true,
  applyToExistingDeals: true
} : undefined;
export const getWizardStepName = memoize(stepIndex => {
  switch (stepIndex) {
    case FORECAST_CATEGORIES_STEP:
      return 'forecast-categories';
    case CATEGORY_MAPPING_STEP:
      return 'category-mapping';
    case FINISH_STEP:
      return 'finish';
    default:
      return '';
  }
});
export const getDuplicateForecastCategoryLabels = forecastCategories => forecastCategories.map(forecastCategory => forecastCategory.label.toLowerCase()).filter((label, index, labels) => labels.indexOf(label) !== index);
export const isInputEmpty = input => input.trim() === '';
export const getUpdatedDealStageMappings = (existingDealStagesMappings, pipelineId, forecastCategoryId, newMappings) => {
  const updated = Object.assign({}, existingDealStagesMappings);
  updated[pipelineId].dealStageByForecastCategory[forecastCategoryId] = newMappings;
  for (const [key, value] of Object.entries(updated[pipelineId].dealStageByForecastCategory)) {
    const duplicateMappings = value.filter(val => newMappings.includes(val));
    if (key !== forecastCategoryId && duplicateMappings.length >= 0) {
      const uniqueMappings = value.filter(val => !newMappings.includes(val));
      updated[pipelineId].dealStageByForecastCategory[key] = uniqueMappings;
    }
  }
  return updated;
};
export const selectedCategoriesAreUsed = (selectedCategories, referencedCategories) => {
  const selectedCategoriesThatAreUsed = selectedCategories.filter(value => referencedCategories.includes(value));
  return selectedCategoriesThatAreUsed.length > 0;
};